import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Select,
  SelectChangeEvent,
  Stack,
  MenuItem,
  Chip,
  Typography
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { OpenInNew, QrCode } from '@mui/icons-material'
import { Device, RuuviTag, RuuviTagInput, RuuviTagSignal } from '../types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { humanizeTimestamp } from '../utils/date'
import { useEffect, useState } from 'react'
import { useAuth } from '../hooks'
import { userIsAdminOfProject, userIsSuperadmin, userIsUserOfProject } from '../utils/auth'
import AssetDetails from './AssetDetails'
import { unpairRuuviTagFromDevice, updateRuuviTag } from '../services/ruuviTagService'
import { getPowerSuppliesByProjectId } from '../services/projectService'
import { formatMacAddress, translateRuuviTagType } from '../utils/ruuviTag'
import { subMinutes } from 'date-fns'
import SignalIndicator from './SignalIndicator'
import { useDispatch } from 'react-redux'
import { createErrorOrSuccessNotification } from '../redux/slices/notifications'
import { NotificationType } from '../enums/NotificationType'
import { AssetStatus } from '../types/Asset'
import { RuuviTagType } from '../enums'

interface RuuviTagDetailsProps {
  ruuviTag: RuuviTag
  onUpdate?: (ruuviTag: RuuviTag) => void
}

export default function RuuviTagDetails({ ruuviTag, onUpdate }: RuuviTagDetailsProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The dispatch function
   */
  const dispatch = useDispatch()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The input.
   */
  const [input, setInput] = useState<RuuviTagInput>({
    device: ruuviTag.deviceName
  })

  /**
   * Indicates if the component is booting.
   */
  const [_isBooting, setIsBooting] = useState<boolean>(true)

  /**
   * Indicates if the component is saving.
   */
  const [isSaving, setIsSaving] = useState<boolean>(false)

  /**
   * The devices of project.
   */
  const [devices, setDevices] = useState<Device[]>([])

  const isAttached = ruuviTag.type === RuuviTagType.ATTACHED

  /**
   * The devices by signal strength.
   */
  const devicesBySignalStrength = devices.sort((a, b) => {
    return getAverageSignal(b.name) - getAverageSignal(a.name)
  })

  /**
   * Indicates if the device dialog is open.
   */
  const [isDeviceDialogOpen, setIsDeviceDialogOpen] = useState<boolean>(false)

  /**
   * Check if the current user can view the device.
   */
  function canViewDevice(device: Device): boolean {
    if (currentUser == null) {
      return false
    }
    return (
      (userIsSuperadmin(currentUser) ||
        (device?.asset?.project &&
          (userIsAdminOfProject(currentUser, device.asset.project) ||
            userIsUserOfProject(currentUser, device.asset.project)))) ??
      false
    )
  }

  /**
   * Check if the current user can edit ruuvi tag.
   */
  function canEditRuuviTag(ruuviTag: RuuviTag): boolean {
    if (currentUser == null) {
      return false
    }
    return (
      (userIsSuperadmin(currentUser) ||
        (ruuviTag?.asset?.project && userIsAdminOfProject(currentUser, ruuviTag.asset.project))) ??
      false
    )
  }

  /**
   * Open the device dialog.
   */
  function openDeviceDialog(): void {
    setIsDeviceDialogOpen(true)
  }

  /**
   * Close the device dialog.
   */
  function closeDeviceDialog(): void {
    setIsDeviceDialogOpen(false)
  }

  /**
   * Cancel the device dialog.
   */
  function cancelDeviceDialog(): void {
    closeDeviceDialog()
    setInput({
      device: ruuviTag.deviceName
    })
  }

  /**
   * Get the signals of a device.
   *
   * @param deviceName The device name.
   *
   * @returns The signals of the device.
   */
  function getSignals(deviceName: string): RuuviTagSignal[] {
    let signals: RuuviTagSignal[] = []

    if (deviceName in ruuviTag.signalHistory) {
      signals = ruuviTag.signalHistory[deviceName]
    }

    // Keep only signals of the last 15 minutes.
    signals = signals.filter((signal) => {
      return new Date(signal.time) > subMinutes(new Date(), 15)
    })

    return signals
  }

  /**
   * Get the average signal of a device.
   *
   * @param deviceName The device name.
   *
   * @returns The average signal of the device.
   */
  function getAverageSignal(deviceName: string): number {
    const signals = getSignals(deviceName)

    if (signals.length === 0) {
      return -255
    }

    const sum = signals.reduce((sum, signal) => sum + signal.rssi, 0)

    return sum / signals.length
  }

  async function boot(signal: AbortSignal): Promise<void> {
    try {
      setIsBooting(true)

      if (ruuviTag?.asset?.projectId) {
        await loadDevices(ruuviTag.asset.projectId, signal)
      }
    } finally {
      setIsBooting(false)
    }
  }

  async function loadDevices(projectId: number, _signal: AbortSignal): Promise<void> {
    const powerSupplies = await getPowerSuppliesByProjectId(projectId)
    const devices = powerSupplies
      .filter((powerSupply) => powerSupply.device !== null && powerSupply.device !== undefined)
      .map((powerSupply) => powerSupply.device as Device)

    setDevices(devices)
  }

  /**
   * Save the ruuvi tag.
   */
  async function save(): Promise<void> {
    try {
      setIsSaving(true)

      if (input.device === null && isAttached) {
        onUpdate?.(await unpairRuuviTagFromDevice(ruuviTag))
      } else {
        onUpdate?.(await updateRuuviTag(ruuviTag.id, input))
      }

      setIsSaving(false)

      // Close the device dialog.
      setIsDeviceDialogOpen(false)
    } catch (_error) {
      // TODO: Handle error.
      dispatch(
        createErrorOrSuccessNotification(NotificationType.WARNING, 'Failed to save the Ruuvi tag!')
      )
      setIsSaving(false)
    }
  }

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      await boot(controller.signal)
    })()

    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      // TODO: Reset the device of ruuvi tag

      if (ruuviTag?.asset?.projectId) {
        await loadDevices(ruuviTag.asset.projectId, controller.signal)
      } else {
        setDevices([])
      }
    })()

    return () => controller.abort()
  }, [ruuviTag.asset?.projectId])

  return (
    <AssetDetails
      asset={ruuviTag?.asset ? { ...ruuviTag?.asset, ruuviTag } : null}
      onUpdate={(asset) => {
        onUpdate?.({ ...ruuviTag, asset })
      }}
    >
      <Divider sx={{ my: 3 }} />

      <List>
        <ListItem>
          <ListItemText primary={translateRuuviTagType(ruuviTag)} secondary={t('ruuvitag:type')} />
        </ListItem>

        {ruuviTag?.asset?.status !== AssetStatus.INACTIVE && (
          <ListItemButton>
            <ListItemText
              primary={
                <>
                  {ruuviTag?.device ? (
                    <Stack direction="row" gap={2} alignItems="center">
                      {ruuviTag.device?.asset?.name ?? ruuviTag.deviceName}
                      {ruuviTag?.device && (
                        <Chip
                          icon={<QrCode fontSize="inherit" />}
                          label={ruuviTag.device.shortUUID}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 2 }}
                        />
                      )}
                      {ruuviTag?.deviceName && (
                        <SignalIndicator
                          rssi={getAverageSignal(ruuviTag.deviceName)}
                          fontSize="small"
                        />
                      )}
                    </Stack>
                  ) : (
                    <Typography color="text.disabled">{t('ruuvitag:noHostDevice')}</Typography>
                  )}
                </>
              }
              secondary={t('hostDevice')}
            />
            <ListItemSecondaryAction>
              <Stack direction="row" gap={2}>
                {ruuviTag.type === RuuviTagType.DEVICE && canEditRuuviTag(ruuviTag) && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ alignSelf: 'center' }}
                    onClick={openDeviceDialog}
                  >
                    {ruuviTag?.device ? t('edit') : t('add')}
                  </Button>
                )}

                {ruuviTag.device && canViewDevice(ruuviTag.device) && (
                  <IconButton onClick={() => navigate(`/devices/${ruuviTag?.device?.name}`)}>
                    <OpenInNew />
                  </IconButton>
                )}
              </Stack>
            </ListItemSecondaryAction>
          </ListItemButton>
        )}

        <Dialog open={isDeviceDialogOpen} onClose={cancelDeviceDialog} fullWidth maxWidth="sm">
          <DialogTitle>{t('selectDevice')}</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="device-select-label">{t('device')}</InputLabel>
              <Select
                id="device-select"
                labelId="device-select-label"
                value={input.device ?? ''}
                label={t('device')}
                autoFocus
                onChange={(event: SelectChangeEvent) => {
                  const nextValue = event.target.value
                  setInput({
                    ...input,
                    device: nextValue === '' ? null : nextValue
                  })
                }}
              >
                {devicesBySignalStrength.map((device) => (
                  <MenuItem value={device.name}>
                    <Stack direction="row" gap={2} justifyContent="flex-start" alignItems="center">
                      <SignalIndicator rssi={getAverageSignal(device.name)} fontSize="small" />
                      {device?.asset?.name ?? device.name}
                      <Chip
                        icon={<QrCode fontSize="inherit" />}
                        label={device.shortUUID}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 2 }}
                      />
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button disabled={isSaving} onClick={cancelDeviceDialog}>
              {t('cancel')}
            </Button>

            {input.device !== null && (
              <Button
                disabled={isSaving}
                onClick={() => {
                  setInput({
                    ...input,
                    device: null
                  })
                }}
              >
                {t('clear')}
              </Button>
            )}

            <LoadingButton
              variant="contained"
              disabled={isSaving}
              loading={isSaving}
              onClick={save}
            >
              <span>{t('save')}</span>
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <ListItem>
          <ListItemText primary={formatMacAddress(ruuviTag.id)} secondary={t('macAddress')} />
        </ListItem>

        <ListItem>
          <ListItemText
            primary={humanizeTimestamp(ruuviTag.createdAt)}
            secondary={t('createdAt')}
          />
        </ListItem>
      </List>
    </AssetDetails>
  )
}
